/**
 * Utility functions for automatic video thumbnail generation
 * Extracts frames from video files to create preview images
 */

/**
 * Generate thumbnail from video file by extracting a frame
 * @param {File} videoFile - The video file to extract thumbnail from
 * @param {number} seekTime - Time in seconds to capture frame (default: 1 second)
 * @returns {Promise<Blob>} - Thumbnail image as Blob
 */
export const generateVideoThumbnail = (videoFile, seekTime = 1) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    let objectURL = null;
    let dimensionsReady = false;
    
    // Timeout to prevent hanging
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Thumbnail generation timeout'));
    }, 15000); // 15 second timeout
    
    const cleanup = () => {
      clearTimeout(timeout);
      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
      video.src = '';
      video.load();
    };
    
    const checkDimensionsAndSeek = () => {
      // Wait for valid dimensions with retry logic
      const checkInterval = setInterval(() => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          clearInterval(checkInterval);
          dimensionsReady = true;
          
          // Set canvas dimensions to match video
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          
          // Seek to specified time (or mid-point if seekTime exceeds duration)
          const targetTime = Math.min(seekTime, video.duration / 2);
          video.currentTime = targetTime;
        }
      }, 50); // Check every 50ms
      
      // Clear interval after 3 seconds if dimensions still not available
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!dimensionsReady) {
          cleanup();
          reject(new Error('Video dimensions not available - unsupported format or corrupted file'));
        }
      }, 3000);
    };
    
    try {
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      
      video.addEventListener('loadedmetadata', () => {
        checkDimensionsAndSeek();
      });
      
      // Fallback: also try on loadeddata event
      video.addEventListener('loadeddata', () => {
        if (!dimensionsReady) {
          checkDimensionsAndSeek();
        }
      });
      
      video.addEventListener('seeked', () => {
        // Add small delay to ensure frame is fully rendered
        setTimeout(() => {
          try {
            // Validate canvas dimensions before drawing
            if (canvas.width === 0 || canvas.height === 0) {
              cleanup();
              reject(new Error('Invalid canvas dimensions'));
              return;
            }
            
            // Draw video frame to canvas
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            // Verify that something was drawn
            const imageData = context.getImageData(0, 0, 1, 1);
            if (!imageData || imageData.data.every(val => val === 0)) {
              cleanup();
              reject(new Error('Failed to capture video frame'));
              return;
            }
            
            // Convert canvas to blob
            canvas.toBlob(
              (blob) => {
                if (blob && blob.size > 0) {
                  cleanup();
                  resolve(blob);
                } else {
                  cleanup();
                  reject(new Error('Failed to generate thumbnail blob'));
                }
              },
              'image/jpeg',
              0.85 // Quality
            );
          } catch (err) {
            cleanup();
            reject(err);
          }
        }, 100); // 100ms delay to ensure frame is ready
      });
      
      video.addEventListener('error', (err) => {
        cleanup();
        reject(new Error('Failed to load video for thumbnail generation'));
      });
      
      // Load video
      objectURL = URL.createObjectURL(videoFile);
      video.src = objectURL;
    } catch (err) {
      cleanup();
      reject(err);
    }
  });
};

/**
 * Generate thumbnail and convert to File object
 * @param {File} videoFile - The video file to extract thumbnail from
 * @param {string} fileName - Name for the thumbnail file
 * @param {number} seekTime - Time in seconds to capture frame
 * @returns {Promise<File>} - Thumbnail as File object
 */
export const generateVideoThumbnailFile = async (videoFile, fileName = 'thumbnail.jpg', seekTime = 1) => {
  try {
    const blob = await generateVideoThumbnail(videoFile, seekTime);
    return new File([blob], fileName, { type: 'image/jpeg' });
  } catch (err) {
    console.error('Error generating video thumbnail file:', err);
    throw err;
  }
};

/**
 * Generate thumbnail and get data URL for preview
 * @param {File} videoFile - The video file to extract thumbnail from
 * @param {number} seekTime - Time in seconds to capture frame
 * @returns {Promise<string>} - Thumbnail as data URL
 */
export const generateVideoThumbnailDataURL = (videoFile, seekTime = 1) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    let objectURL = null;
    let dimensionsReady = false;
    
    // Timeout to prevent hanging
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Thumbnail generation timeout'));
    }, 15000);
    
    const cleanup = () => {
      clearTimeout(timeout);
      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
      video.src = '';
      video.load();
    };
    
    const checkDimensionsAndSeek = () => {
      const checkInterval = setInterval(() => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          clearInterval(checkInterval);
          dimensionsReady = true;
          
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const targetTime = Math.min(seekTime, video.duration / 2);
          video.currentTime = targetTime;
        }
      }, 50);
      
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!dimensionsReady) {
          cleanup();
          reject(new Error('Video dimensions not available - unsupported format or corrupted file'));
        }
      }, 3000);
    };
    
    try {
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      
      video.addEventListener('loadedmetadata', () => {
        checkDimensionsAndSeek();
      });
      
      video.addEventListener('loadeddata', () => {
        if (!dimensionsReady) {
          checkDimensionsAndSeek();
        }
      });
      
      video.addEventListener('seeked', () => {
        setTimeout(() => {
          try {
            if (canvas.width === 0 || canvas.height === 0) {
              cleanup();
              reject(new Error('Invalid canvas dimensions'));
              return;
            }
            
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataURL = canvas.toDataURL('image/jpeg', 0.85);
            
            if (!dataURL || dataURL === 'data:,') {
              cleanup();
              reject(new Error('Failed to generate thumbnail data URL'));
              return;
            }
            
            cleanup();
            resolve(dataURL);
          } catch (err) {
            cleanup();
            reject(err);
          }
        }, 100);
      });
      
      video.addEventListener('error', (err) => {
        cleanup();
        reject(new Error('Failed to load video for thumbnail generation'));
      });
      
      objectURL = URL.createObjectURL(videoFile);
      video.src = objectURL;
    } catch (err) {
      cleanup();
      reject(err);
    }
  });
};